import { type default as React, useEffect } from 'react';

import {
	AnalyticsEventActions,
	AnalyticsEventSource,
	type AppName,
	type ProductNamePFDirectory,
	useAnalytics,
	usePrincipalUser,
	useSettings,
	useSite,
} from '@atlassian/ptc-common';

import { SliAnalytics } from '../../constants/sli-constants';

export interface AppInitialDataRequestsProps {
	appName: AppName;
	children: React.ReactElement;
}

const mapAppNameToProduct: Record<AppName, ProductNamePFDirectory | undefined> = {
	'': undefined,
	'site-directory': undefined,
	'confluence-directory': 'CONFLUENCE',
	'jira-directory': 'JIRA',
	'teamcentral-directory': 'TOWNSQUARE',
	'home-directory': 'HOME',
};

export function AppInitialDataRequests(props: AppInitialDataRequestsProps) {
	const { appName, children } = props;

	const [{ cloudId }] = useSite();
	const [{ principalUserId }, { getPrincipalUserInfo }] = usePrincipalUser();

	const analytics = useAnalytics();

	const [{ hasLoadedSettings, hasErrorOnFetchSettings }, { getSettings }] = useSettings();

	useEffect(() => {
		analytics.startUIViewedEvent(principalUserId);
	}, [analytics, principalUserId]);

	useEffect(() => {
		if (hasLoadedSettings) {
			analytics.pushOperationalEvent({
				action: hasErrorOnFetchSettings
					? AnalyticsEventActions.FAILED
					: AnalyticsEventActions.SUCCEEDED,
				actionSubject: SliAnalytics.GetSettings,
				source: AnalyticsEventSource.DIRECTORY_APP,
			});
		}
	}, [analytics, hasLoadedSettings, hasErrorOnFetchSettings]);

	const product = mapAppNameToProduct[appName];

	useEffect(() => {
		// currently, cloudId is a required prop of Embedded Directory
		if (!hasLoadedSettings && cloudId) {
			void getSettings(product, cloudId);
		}
	}, [product, hasLoadedSettings, getSettings, cloudId]);

	useEffect(() => {
		// currently, cloudId is a required prop of Embedded Directory
		if (cloudId) {
			void getPrincipalUserInfo(cloudId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cloudId]);

	return children;
}

export default AppInitialDataRequests;
